import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchApplicationInfoMapping } from '@/services/applicationInfo';
import {
  fetchProductivityApps,
  saveProductivityApps,
  fetchTeamProductivityApps,
  saveTeamProductivityApps,
  resetTeamProductivityApps,
} from '@/services/productivityApps';
import { queryKeys } from '@/config/queryKeys';

/**
 * selectedTeamId = null  → editing company defaults
 * selectedTeamId = string → editing that team's override
 *
 * Server state lives in React Query cache — parallel callers (e.g. the focus
 * widget on the dashboard) share one in-flight request and cache entry.
 * On save/reset, invalidateQueries refetches all consumers automatically.
 */
export function useProductivityApps(companyId, selectedTeamId) {
  const isTeamContext = selectedTeamId != null;
  const queryClient = useQueryClient();

  // ── Server state (React Query) ──────────────────────────────────────────────

  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const { data: companyData, isLoading: isLoadingCompany } = useQuery({
    queryKey: queryKeys.productivityApps.company(companyId),
    queryFn: () => fetchProductivityApps(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: teamData, isLoading: isLoadingTeam } = useQuery({
    queryKey: queryKeys.productivityApps.team(selectedTeamId),
    queryFn: () => fetchTeamProductivityApps(selectedTeamId),
    enabled: !!selectedTeamId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Derived server values for the active context
  const serverFocus = isTeamContext
    ? (teamData?.focusApps ?? [])
    : (companyData?.focusApps ?? []);
  const serverDistraction = isTeamContext
    ? (teamData?.distractionApps ?? [])
    : (companyData?.distractionApps ?? []);
  const teamHasOverride = isTeamContext
    ? (teamData?.hasOverride ?? false)
    : false;

  // ── Local edit state ────────────────────────────────────────────────────────

  const [focusApps, setFocusApps] = useState([]);
  const [distractionApps, setDistractionApps] = useState([]);

  // Sync edit state whenever context switches or fresh server data arrives.
  // refetchOnWindowFocus: false + 5-min staleTime prevent background refetches
  // from wiping unsaved edits during normal use.
  useEffect(() => {
    setFocusApps([...serverFocus]);
    setDistractionApps([...serverDistraction]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, companyData, teamData]);

  const hasChanges = useMemo(() => {
    const sameSet = (a, b) => {
      if (a.length !== b.length) return false;
      const sa = [...a].map((v) => v.toLowerCase()).sort();
      const sb = [...b].map((v) => v.toLowerCase()).sort();
      return sa.every((v, i) => v === sb[i]);
    };
    return (
      !sameSet(focusApps, serverFocus) ||
      !sameSet(distractionApps, serverDistraction)
    );
  }, [focusApps, distractionApps, serverFocus, serverDistraction]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const saveMutation = useMutation(
    ({ isTeam, teamId, cId, apps }) =>
      isTeam
        ? saveTeamProductivityApps(teamId, apps)
        : saveProductivityApps(cId, apps),
    {
      onSuccess: (_, { isTeam, teamId, cId }) => {
        queryClient.invalidateQueries(
          isTeam
            ? queryKeys.productivityApps.team(teamId)
            : queryKeys.productivityApps.company(cId)
        );
      },
    }
  );

  const discardChanges = useCallback(() => {
    setFocusApps([...serverFocus]);
    setDistractionApps([...serverDistraction]);
  }, [serverFocus, serverDistraction]);

  const save = useCallback(async () => {
    if (saveMutation.isLoading) return { success: false };
    try {
      await saveMutation.mutateAsync({
        isTeam: isTeamContext,
        teamId: selectedTeamId,
        cId: companyId,
        apps: { focusApps, distractionApps },
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }, [
    saveMutation,
    isTeamContext,
    selectedTeamId,
    companyId,
    focusApps,
    distractionApps,
  ]);

  const resetMutation = useMutation(
    (teamId) => resetTeamProductivityApps(teamId),
    {
      onSuccess: (_, teamId) => {
        queryClient.invalidateQueries(queryKeys.productivityApps.team(teamId));
      },
    }
  );

  const resetTeamOverride = useCallback(async () => {
    if (!selectedTeamId || resetMutation.isLoading) return { success: false };
    try {
      await resetMutation.mutateAsync(selectedTeamId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }, [resetMutation, selectedTeamId]);

  // ── Known app names ─────────────────────────────────────────────────────────

  const knownAppNames = useMemo(() => {
    return [
      ...new Set(
        Object.values(appNameMapping).filter(
          (v) => typeof v === 'string' && v.trim()
        )
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [appNameMapping]);

  return {
    isLoading: !!companyId && isLoadingCompany,
    isLoadingTeam,
    isSaving: saveMutation.isLoading || resetMutation.isLoading,
    hasChanges,
    knownAppNames,
    focusApps,
    distractionApps,
    setFocusApps,
    setDistractionApps,
    teamHasOverride,
    save,
    discardChanges,
    resetTeamOverride,
  };
}
